<template>
    <div class="stock-trend-container">
        <!-- 分时图顶部信息栏与快速切换 -->
        <div class="stock-trend-header d-flex align-items-center justify-content-between flex-wrap px-2 py-1 border-bottom">
            <div class="stock-trend-info d-flex align-items-center flex-wrap mr-2">
                <!-- 股票名称与代码 -->
                <span class="stock-trend-name font-weight-bold mr-2">{{ displayTitle }}</span>
                <span v-if="subText && subText !== displayTitle" class="text-muted small mr-2">{{ subText }}</span>
                
                <!-- 分时图对应日期 -->
                <span class="trend-date-badge badge mr-2 px-2 py-1">
                    {{ displayDate }}
                </span>

                <!-- 价格、涨跌价、涨跌幅、均价 -->
                <div v-if="trendStats.hasData" class="d-inline-flex align-items-center flex-wrap mr-2 font-weight-bold" :class="trendStats.colorClass" style="font-size: 13px;">
                    <span class="mr-2">价格: {{ trendStats.price }}</span>
                    <span class="mr-2">涨跌: {{ trendStats.change }}</span>
                    <span class="mr-2">涨幅: {{ trendStats.pct }}</span>
                    <span v-if="trendStats.avg" class="text-warning font-weight-normal small mr-2">均价: {{ trendStats.avg }}</span>
                </div>
                <span v-else-if="isLoading" class="text-muted small">加载分时中...</span>
                <span v-else class="text-muted small">暂无当日分时数据</span>
            </div>

            <!-- 分时图快捷前一天 / 后一天切换 -->
            <div class="stock-trend-actions d-inline-flex align-items-center my-1 ml-auto">
                <b-button-group size="sm">
                    <b-button 
                        size="sm" 
                        variant="outline-secondary" 
                        class="px-2 py-0 trend-nav-btn" 
                        :disabled="!canPrev"
                        @click="onPrevDay"
                        title="查看前一天分时"
                    >
                        ◀ 前一天
                    </b-button>
                    <b-button 
                        size="sm" 
                        variant="outline-secondary" 
                        class="px-2 py-0 trend-nav-btn" 
                        :disabled="!canNext"
                        @click="onNextDay"
                        title="查看后一天分时"
                    >
                        后一天 ▶
                    </b-button>
                </b-button-group>
            </div>
        </div>

        <!-- 分时图 ECharts 图表 -->
        <v-chart-stock-trend :style="chart_style()" :options="line" :autoresize="true" />
    </div>
</template>

<script>
	import ECharts from 'vue-echarts'
	import axios from 'axios'
	import {
		request_zzquant
	} from "network/request.js"

	const upColor = '#ec0000';
	const downColor = '#00da3c';

	export default {
		props: {
			title: String,
			subText: String,
			symbol_code: String,
			chart_heigh: { type: String, default: '350px' },
			chart_width: { type: String, default: '100%' },
			selected_date: String, // prop接收选中的日期
			canPrev: { type: Boolean, default: true },
			canNext: { type: Boolean, default: true }
		},
		watch: {
			symbol_code: 'refresh',
			selected_date: 'refresh' // 监听日期变化，重新加载数据
		},
		computed: {
			displayTitle() {
				return this.title || this.symbol_code || '';
			},
			displayDate() {
				if (this.trendStats.date) return this.trendStats.date;
				if (this.selected_date) {
					const s = String(this.selected_date).trim();
					if (/^\d{8}$/.test(s)) {
						return s.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
					}
					return s;
				}
				return '';
			}
		},
		components: {
			'v-chart-stock-trend': ECharts
		},
		data() {
			return {
				isLoading: false,
				trendStats: {
					hasData: false,
					price: '--',
					change: '--',
					pct: '--',
					avg: '',
					colorClass: 'text-muted',
					date: ''
				},
				line: {
					animation: false,
					animationDuration: 0,
					animationDurationUpdate: 0,
					title: {
						show: false
					},
					tooltip: {
						trigger: 'axis',
						axisPointer: {
							type: 'cross',
							lineStyle: {
								color: '#777777',
								type: 'dashed'
							}
						}
					},
					axisPointer: {
						link: { xAxisIndex: 'all' }
					},
					legend: {
						show: false,
						data: ['价格', '均价', '量能']
					},
					visualMap: [{
						show: false,
						seriesIndex: 2,
						dimension: 2,
						pieces: [{
							value: 0,
							color: '#999999'
						}, {
							value: 1,
							color: upColor
						}, {
							value: 2,
							color: downColor
						}]
					}],
					grid: [
						{
							left: 55,
							right: 55,
							top: 15,
							height: '58%'
						},
						{
							left: 55,
							right: 55,
							top: '76%',
							height: '18%'
						}
					],
					xAxis: [
						{
							type: 'category',
							scale: true,
							boundaryGap: false,
							splitLine: {
								show: false
							},
							axisPointer: {
								z: 100
							},
							axisLabel: {
								interval: 29,
								formatter: function(value) {
									return value === '09:30' ? '    09:30' : (value === '15:00' ? '15:00    ' : value);
								},
								fontSize: 10,
								color: '#999999'
							},
							axisLine: {
								onZero: false,
								lineStyle: {
									color: '#666666'
								}
							}
						},
						{
							gridIndex: 1,
							type: 'category',
							scale: true,
							boundaryGap: false,
							axisLine: {
								onZero: false,
								lineStyle: {
									color: '#666666'
								}
							},
							axisTick: {
								show: false
							},
							splitLine: {
								show: false
							},
							axisLabel: {
								show: false
							}
						}
					],
					yAxis: [
						{
							// yAxisIndex: 0 左侧 - 价格 (Price)
							type: 'value',
							position: 'left',
							scale: false,
							splitLine: {
								show: true,
								lineStyle: {
									color: '#2a2e39',
									type: 'dashed'
								}
							},
							splitArea: {
								show: false
							},
							axisLine: {
								lineStyle: {
									color: '#666666',
									width: 1
								}
							},
							axisLabel: {
								formatter: (value) => Number(value).toFixed(2),
								fontSize: 10,
								color: '#999999'
							}
						},
						{
							// yAxisIndex: 1 成交量 (Volume)
							gridIndex: 1,
							position: 'right',
							scale: true,
							splitNumber: 2,
							axisLabel: {
								show: false
							},
							axisLine: {
								show: false
							},
							axisTick: {
								show: false
							},
							splitLine: {
								show: false
							}
						},
						{
							// yAxisIndex: 2 右侧 - 涨跌幅 (Percentage)
							type: 'value',
							position: 'right',
							gridIndex: 0,
							scale: false,
							splitLine: {
								show: false
							},
							axisLine: {
								lineStyle: {
									color: '#666666',
									width: 1
								}
							},
							axisLabel: {
								formatter: (value) => {
									const n = Number(value);
									if (Math.abs(n) < 0.005) return '0.00%';
									return (n > 0 ? '+' : '') + n.toFixed(2) + '%';
								},
								fontSize: 10,
								color: '#999999'
							}
						}
					],
					series: []
				}
			};
		},
		mounted() {
			this.refresh();
		},
		methods: {
			onPrevDay() {
				this.$emit('prev-day');
			},
			onNextDay() {
				this.$emit('next-day');
			},
			chart_style() {
				return {
					width: this.chart_width,
					height: this.chart_heigh
				};
			},
			refresh() {
				if (!this.symbol_code || this.symbol_code.length !== 6) return;
				this.isLoading = true;

				let url1 = '/v3/market/trend/' + this.symbol_code;
				if (this.selected_date) {
					url1 = url1 + '/date/' + this.selected_date;
				}

				axios.all([
					request_zzquant({ url: url1 })
				])
				.then(axios.spread((resp1) => {
					this.isLoading = false;
					const rawData = resp1 && resp1['data'] && resp1['data']['data'] ? resp1['data']['data'] : {};
					const splitDataItem = this.splitData(rawData);
					const preclose = Number(rawData['preclose_px']) || 0;

					// 计算坐标轴边界与间隔（对称中心为昨收价与0%）
					let minPrice = Infinity;
					let maxPrice = -Infinity;
					splitDataItem.values_price.forEach(p => {
						const num = Number(p);
						if (!isNaN(num) && num > 0) {
							if (num > maxPrice) maxPrice = num;
							if (num < minPrice) minPrice = num;
						}
					});

					let maxPctDiff = 0;
					if (preclose > 0 && maxPrice > -Infinity && minPrice < Infinity) {
						const diffHigh = Math.abs(maxPrice - preclose) / preclose * 100;
						const diffLow = Math.abs(minPrice - preclose) / preclose * 100;
						maxPctDiff = Math.max(diffHigh, diffLow);
					}

					let boardLimit = 10;
					const codeStr = String(this.symbol_code || '');
					if (codeStr.startsWith('30') || codeStr.startsWith('68')) {
						boardLimit = 20;
					} else if (codeStr.startsWith('8') || codeStr.startsWith('4') || codeStr.startsWith('92')) {
						boardLimit = 30;
					}

					let maxPct = boardLimit;
					if (maxPctDiff > boardLimit) {
						maxPct = Math.ceil(maxPctDiff * 1.05 * 10) / 10;
					} else if (maxPctDiff === 0 && preclose === 0) {
						maxPct = 10;
					}

					const pctInterval = maxPct / 2;
					const minPctBound = -maxPct;
					const maxPctBound = maxPct;

					const maxPriceBound = preclose > 0 ? (preclose * (1 + maxPct / 100)) : (maxPrice > 0 ? maxPrice * 1.1 : 10);
					const minPriceBound = preclose > 0 ? (preclose * (1 - maxPct / 100)) : (minPrice > 0 ? minPrice * 0.9 : 0);
					const priceInterval = preclose > 0 ? (preclose * (pctInterval / 100)) : ((maxPriceBound - minPriceBound) / 4);

					// 配置左侧价格坐标轴
					this.line.yAxis[0].min = Number(minPriceBound.toFixed(2));
					this.line.yAxis[0].max = Number(maxPriceBound.toFixed(2));
					this.line.yAxis[0].interval = Number(priceInterval.toFixed(4));
					this.line.yAxis[0].axisLabel.color = (val) => {
						const num = Number(val);
						if (Math.abs(num - preclose) < 0.005) return '#888888';
						return num > preclose ? upColor : downColor;
					};

					// 配置右侧涨跌幅坐标轴
					this.line.yAxis[2].min = Number(minPctBound.toFixed(2));
					this.line.yAxis[2].max = Number(maxPctBound.toFixed(2));
					this.line.yAxis[2].interval = Number(pctInterval.toFixed(2));
					this.line.yAxis[2].axisLabel.color = (val) => {
						const num = Number(val);
						if (Math.abs(num) < 0.005) return '#888888';
						return num > 0 ? upColor : downColor;
					};

					// 配置 Tooltip 悬浮详情
					this.line.tooltip.formatter = (params) => {
						if (!params || params.length === 0) return '';
						const time = params[0].name || '';
						let priceVal = null;
						let avgVal = null;
						let volVal = null;
						params.forEach(p => {
							if (p.seriesName === '价格' || p.seriesName === '分时' || p.seriesName === 'K') {
								priceVal = p.value;
							} else if (p.seriesName === '均价' || p.seriesName === 'AVG') {
								avgVal = p.value;
							} else if (p.seriesName === '量能' || p.seriesName === 'V') {
								volVal = Array.isArray(p.value) ? p.value[1] : p.value;
							}
						});
						if (priceVal == null) return time;
						const pNum = Number(priceVal);
						const diff = pNum - preclose;
						const diffPct = preclose > 0 ? (diff / preclose * 100) : 0;
						const color = diff > 0 ? upColor : (diff < 0 ? downColor : '#cccccc');
						const sign = diff > 0 ? '+' : '';

						let html = `<div style="font-size:12px;line-height:1.6;min-width:130px;">`;
						html += `<div>时间: <b>${time}</b></div>`;
						html += `<div>价格: <b style="color:${color}">${pNum.toFixed(2)}</b></div>`;
						html += `<div>涨跌: <b style="color:${color}">${sign}${diff.toFixed(2)} (${sign}${diffPct.toFixed(2)}%)</b></div>`;
						if (avgVal != null && !isNaN(avgVal) && Number(avgVal) > 0) {
							html += `<div style="color:#ffc04c">均价: <b>${Number(avgVal).toFixed(2)}</b></div>`;
						}
						if (volVal != null && !isNaN(volVal)) {
							html += `<div>成交量: <b>${Number(volVal).toLocaleString()}手</b></div>`;
						}
						html += `</div>`;
						return html;
					};

					this.line.xAxis[0].data = splitDataItem.categoryData;
					this.line.xAxis[1].data = splitDataItem.categoryData;

					const series_data = [
						{
							name: '价格',
							type: 'line',
							animation: false,
							showSymbol: false,
							symbol: 'circle',
							data: splitDataItem.values_price,
							yAxisIndex: 0,
							lineStyle: {
								normal: { width: 1.5, color: '#3399ff' }
							},
							areaStyle: {
								normal: {
									color: {
										type: 'linear',
										x: 0, y: 0, x2: 0, y2: 1,
										colorStops: [
											{ offset: 0, color: 'rgba(51, 153, 255, 0.22)' },
											{ offset: 1, color: 'rgba(51, 153, 255, 0.01)' }
										]
									}
								}
							},
							markLine: {
								silent: true,
								symbol: 'none',
								data: [{
									yAxis: preclose,
									lineStyle: { color: '#666666', type: 'dashed', width: 1 },
									label: { show: false }
								}]
							}
						},
						{
							name: '均价',
							type: 'line',
							animation: false,
							showSymbol: false,
							symbol: 'circle',
							smooth: true,
							yAxisIndex: 0,
							data: splitDataItem.values_price_avg,
							lineStyle: {
								normal: { width: 1.2, color: '#ffc04c' }
							}
						},
						{
							name: '量能',
							type: 'bar',
							animation: false,
							xAxisIndex: 1,
							yAxisIndex: 1,
							data: splitDataItem.values_volume
						}
					];

					this.line.series = series_data;

					// 统计顶部信息栏数据
					const trendList = rawData['trend'] || [];
					if (trendList.length > 0) {
						const lastTrend = trendList[trendList.length - 1];
						const latestPrice = Number(lastTrend[1]);
						const latestAvg = Number(lastTrend[2]);
						const diff = latestPrice - preclose;
						const diffPct = preclose > 0 ? (diff / preclose * 100) : 0;
						const sign = diff > 0 ? '+' : '';
						const colorClass = diff > 0 ? 'text-danger' : (diff < 0 ? 'text-success' : 'text-muted');

						let rawDay = String(rawData['day'] || this.selected_date || '');
						let formattedDay = rawDay;
						if (/^\d{8}$/.test(rawDay)) {
							formattedDay = rawDay.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
						}

						this.trendStats = {
							hasData: true,
							price: latestPrice.toFixed(2),
							change: `${sign}${diff.toFixed(2)}`,
							pct: `${sign}${diffPct.toFixed(2)}%`,
							avg: latestAvg > 0 ? latestAvg.toFixed(2) : '',
							colorClass: colorClass,
							date: formattedDay
						};
					} else {
						this.trendStats = {
							hasData: false,
							price: '--',
							change: '--',
							pct: '--',
							avg: '',
							colorClass: 'text-muted',
							date: this.selected_date || ''
						};
					}
				}))
				.catch(err => {
					this.isLoading = false;
					console.log(err);
				});
			},
			splitData(rawData) {
				let categoryData = [];
				let values_price = [];
				let values_price_avg = [];
				let values_volume = [];
				const preclose = Number(rawData['preclose_px']) || 0;
				let prevPrice = preclose;

				if (rawData && rawData['trend']) {
					rawData['trend'].forEach((trend, idx) => {
						categoryData.push(trend[0]);
						const p = Number(trend[1]);
						const avg = Number(trend[2]);
						const vol = Number(trend[3]);
						values_price.push(p);
						values_price_avg.push(avg);

						let dir = 0;
						if (p > prevPrice) dir = 1;
						else if (p < prevPrice) dir = 2;
						values_volume.push([idx, vol, dir]);
						prevPrice = p;
					});
				}

				return {
					categoryData: categoryData,
					values_price: values_price,
					values_price_avg: values_price_avg,
					values_volume: values_volume
				};
			}
		}
	}
</script>

<style scoped>
.stock-trend-container {
	position: relative;
	width: 100%;
}
.stock-trend-header {
	background: var(--bg-card, rgba(255, 255, 255, 0.03));
	border-color: var(--border-color, rgba(128, 128, 128, 0.2)) !important;
	user-select: none;
}
.trend-date-badge {
	background: var(--bg-muted, #343a40);
	color: var(--text-main, #ffffff);
	border: 1px solid var(--border-color, rgba(255, 255, 255, 0.15));
}
body.theme-light .trend-date-badge {
	background: #e9ecef;
	color: #212529;
	border-color: #ced4da;
}
.trend-nav-btn {
	font-size: 12px;
	line-height: 1.5;
	padding: 1px 8px !important;
	border-color: var(--border-color, #6c757d);
	color: var(--text-main, #adb5bd);
}
.trend-nav-btn:hover:not(:disabled) {
	background: var(--primary, #007bff);
	color: #ffffff;
	border-color: var(--primary, #007bff);
}
</style>

